import type {
  PlatformIndexCheckpointSummary,
  PlatformIndexDeadLetterSummary,
  PlatformIndexRebuildSummary,
} from "@tdelivr/api-client/generated/search/search-indexer.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow, type DetailField } from "./board.ts";

/**
 * What the search index control board does to the three listings it reads, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `search`, against **three** operations that
 * SEA-02 published together under **one** scope —
 * [ADR 0064](../../../docs/adr/0064-a-projections-lag-was-always-a-duration-and-the-platform-index-board-is-three-listings-under-one-scope.md):
 *
 *   - `GET /v1/platform/index-checkpoints` — where each projection has got to;
 *   - `GET /v1/platform/index-rebuilds` — what is being rebuilt;
 *   - `GET /v1/platform/index-dead-letters` — what could not be projected.
 *
 * All three are SEA-02's **first listings of any kind**, and its first public operations that answer
 * anything but its own identity.
 *
 * ```
 * search:['Search index control','SEA-02',
 *   'Projection checkpoints, dead-lettered events and rebuilds for every search index.',
 *   [['Indexes','12','done','healthy'],['Lag','42 s','caution','orders index'],
 *    ['Rebuilding','1','active','drivers'],['Dead-lettered','2','failed','']],
 *   ['Index','Checkpoint','Lag'],
 *   [['orders','offset 1,882,104','42 s','caution','lagging'],
 *    ['drivers','rebuild 61%','—','active','rebuilding'],
 *    ['merchants','offset 88,210','1 s','done','current']],
 *   'info','A rebuild never removes the live index until the new one is caught up.']
 * ```
 *
 * ## The lag was always a duration, and the subtraction is this file's
 *
 * `navigation.ts` records this page as blocked partly because "nothing publishes a checkpoint's lag
 * as a duration", naming `checkpoint.lagSeconds`, `checkpoint.lagMilliseconds` and
 * `index.lagSeconds`; `docs/design/CONSOLE_SCREEN_SUPPORT.md` carries the same verdict. **It is true
 * of the name and false of the thing**, which is what ADR 0064 §Context exists to correct:
 * `indexedThrough` is set to the **source event's own `occurredAt`**, so `now − indexedThrough` is
 * the projection lag in seconds — the design's `42 s`, exactly. The service deliberately does not
 * compute it, because a lag computed in a service is stale before it is rendered (ADR 0064 §1, and
 * ADR 0053's rejected "derive it in the service"). So the subtraction is the board's and it is here.
 *
 * ## One row per alias, over a table keyed by the triple — Q-220
 *
 * The design draws one row per index alias with **no tenant beside it**, and that is legitimate:
 * `IndexSchema[]` is parsed from `SEARCH_INDEX_SCHEMAS_JSON` at startup, so the aliases are platform
 * configuration rather than tenant data (ADR 0064 §Context).
 *
 * The checkpoint table's grain is `(tenant_id, index_alias, source)`, which is **not** that. ADR
 * 0064 §4 publishes at the table's grain and says the roll-up is the board's, for two reasons it
 * records rather than decides: a roll-up computed in the service would be a second statement of a
 * rule the design owns, and a listing at the design's grain has no cursor, so it could not be paged
 * at all. ADR 0064 §8 names the rule — "the board rolls up to the worst lag per alias" — and that is
 * `rollUp` below.
 *
 * **The two cells describe one checkpoint row, not an average of several.** The `Lag` cell is the
 * worst lag across every tenant and source projecting into that alias, and the `Checkpoint` cell is
 * **that same row's** offset. An offset from one checkpoint beside a lag from another would be two
 * tenants' rows rendered as one. The panel says how many rows and how many tenants were rolled up,
 * because an operator reading `orders` has no other way to learn that it is five tenants' `orders`.
 *
 * ## What is withheld, and why none of it costs this board a cell
 *
 * ADR 0064 §5: the indexed document's **`fields`** (one tenant's own order, driver and merchant
 * records, never stored and read back by nothing); any **per-tenant document count** (a direct
 * measure of one operator's trading volume, on a board whose tenants compete); **query terms**;
 * `rebuild_requests.`**`reason`** (free-hand prose about one tenant's incident); the rebuild's
 * **idempotency key** and **request fingerprint**; `projection_inbox.`**`last_error_code`** — which
 * is the one that is not obvious, because it is `error.message.slice(0, 100)` from whatever the
 * index writer threw and nothing constrains it to be free of document content; and the audit log's
 * **`actor_subject`** and **`details`**.
 *
 * None of them is a cell of this design. What the design asks for and nothing answers is below.
 *
 * ## The three things this board cannot draw
 *
 *   - **`rebuild 61%`.** `rebuild_requests.status` is `requested | engine_reset` and nothing else;
 *     no percentage exists in the table, the domain or the engine writer. The `Checkpoint` cell
 *     draws the word `rebuilding` and no figure, which is ADR 0064 §8's own decision, because a
 *     made-up percentage on a platform board is a number an operator would act on. **Q-221**.
 *   - **`Indexes · 12 · healthy`**, and the other three tiles. The tile row is drawn on no board of
 *     this console — no listing publishes a total, so a tile reading `12` would mean "12 so far"
 *     under a label meaning "12" (R-04, Q-178). The word `healthy` is a second absence inside the
 *     first: ADR 0064 §8 records that nothing models an index's health.
 *   - **The threshold between `lagging` and `current`.** See `stateOf`. The lag is measured; the
 *     line it is measured against is not published anywhere, and this board reuses the console's own
 *     `STALE_AFTER_MS` rather than inventing a second number.
 *
 * And one the design draws on **another** page: `Blocked projections · 1 · delivery projection` is a
 * tile of `replay`, not of this page. It is SEA-02's data on EVT-04's board and it is not drawn here.
 */

/**
 * The scope all three of SEA-02's platform listings are bought with, named once.
 *
 * **One scope for three operations**, which is ADR 0064 §2's decision and not a convenience: ADR
 * 0039 §2's argument is about what a scope *grants*, and these grant the same thing — one board's
 * three grains of one record. Three scopes would have been three realm entries and three console
 * literals for one page, which ADR 0049 would then have to account for.
 *
 * A literal in this application's source, which is what grants it: `channel-scopes.mjs` derives
 * `tdelivr-platform-console`'s realm grant from the scope strings this console's own files name, so
 * naming it here is the grant and nothing else is (ADR 0032's mechanism, ADR 0049's rule).
 * `search-indexer:read` buys the tenant-bounded checkpoint read and is **not** widened; `:project`
 * and `:rebuild` are writes.
 *
 * The literal lives in `navigation.ts`, where all eighteen of this console's cross-boundary
 * scopes are collected so that "what can this console reach" is answerable from one file.
 * Re-exported here so a reader of this module finds it beside the reasoning, and so
 * `channel-scopes.mjs` sees one literal rather than two.
 *
 * **Q-139 takes another occasion here.** Nobody has decided who may hold a scope that reads across
 * every tenant on the platform.
 */
export { SEARCH_INDEX_SCOPE } from "./navigation.ts";

/** The words the design's three row badges use, plus the one its fourth tile supplies. */
export type IndexState = "rebuilding" | "dead-lettered" | "lagging" | "current";

/** The states this board can reach, in the order `stateOf` decides them. */
export const INDEX_STATES: readonly IndexState[] = [
  "rebuilding",
  "dead-lettered",
  "lagging",
  "current",
];

/**
 * How far behind a projection may be before this board stops calling it `current`.
 *
 * **This is the one value on this board with no published source, and it is not a new number.** The
 * design's `State` badge says `lagging` beside `42 s` and `current` beside `1 s`, so a line exists
 * somewhere in between — and nothing in SEA-02 models one. A grep for `lag`, `threshold` and
 * `stale` across `services/search/search-indexer/**` finds the word `lag` only in prose explaining
 * that the subtraction is the board's. No freshness objective, no per-alias target, nothing.
 *
 * Rather than invent a figure, this is `view-state.ts`'s `STALE_AFTER_MS` — the console's own
 * already-recorded answer to "how old may a live reading be before the screen stops claiming it is
 * current", written for driver location and reused here because it is the same question about the
 * same word. It falls inside the bound the design's own two rows set, `(1 s, 42 s]`.
 *
 * It is a **rendering** threshold and it is said so on every badge's `title`: the duration itself is
 * measured and is on screen beside it, so an operator who disagrees with the line can read the
 * number. What would remove this note is a published freshness objective per index alias — which is
 * OBS-01's shape of thing rather than SEA-02's, and which nothing on this platform publishes today.
 */
export const CURRENT_WITHIN_MS = 6_000;

/** The design's own duration grammar: `42 s`, `1 s`, `2 min`, `9 h`, `3 h`. */
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * A span of milliseconds in the design's own units.
 *
 * Four of them are read straight off this design file — `42 s` and `1 s` on this very page, `2 min`
 * in the notifications dropdown, `9 h` and `3 h` on the workflow and dead-letter pages. The fifth,
 * `d`, is this console's extension for a span past a day, which no page of the design happens to
 * draw; a projection stuck for a week would otherwise read `168 h`.
 *
 * Floored rather than rounded, because a lag of 59.9 s is not a minute yet and a board that said so
 * would be the only place on screen claiming more than it measured.
 */
export function asSpan(ms: number): string {
  const span = Math.max(0, ms);
  if (span < MINUTE_MS) return `${String(Math.floor(span / 1000))} s`;
  if (span < HOUR_MS) return `${String(Math.floor(span / MINUTE_MS))} min`;
  if (span < DAY_MS) return `${String(Math.floor(span / HOUR_MS))} h`;
  return `${String(Math.floor(span / DAY_MS))} d`;
}

/**
 * The design's `offset 1,882,104`: its own word, and its own thousands separators.
 *
 * `en-GB` grouping, which is what the design writes. `lastSequence` is a `bigint` column that both
 * the statement and the mapper take through `::text` and `Number` (ADR 0064 §4), so it arrives as a
 * number here and is formatted rather than concatenated.
 */
const grouped = new Intl.NumberFormat("en-GB");

/**
 * An instant as the panel writes it: the design's date, then a 24-hour clock.
 *
 * UTC, for `board.ts`'s own recorded reason — every instant on these boards is published as UTC and
 * the design names no zone anywhere, which Q-24 records. The clock is here rather than in
 * `board.ts` because this board is the first to need a time of day in a panel and `board.ts` is
 * shared with five measured screens.
 */
const clock = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

export function asMoment(instant: string): string {
  const parsed = Date.parse(instant);
  if (!Number.isFinite(parsed)) return instant;
  return `${asDay(instant)} · ${clock.format(new Date(parsed))}`;
}

/** The three listings, each `undefined` when its call did not answer. */
export interface IndexListings {
  readonly checkpoints: readonly PlatformIndexCheckpointSummary[] | undefined;
  readonly rebuilds: readonly PlatformIndexRebuildSummary[] | undefined;
  readonly deadLetters: readonly PlatformIndexDeadLetterSummary[] | undefined;
}

/** Everything the three listings say about one index alias, after the roll-up. */
export interface IndexRollUp {
  readonly alias: string;
  readonly checkpoints: readonly PlatformIndexCheckpointSummary[];
  /**
   * The checkpoint the `Checkpoint` and `Lag` cells both come from: the alias's worst lag.
   *
   * `undefined` when the alias has no checkpoint at all — which is the ordinary state of an alias
   * mid-rebuild, because `completeRebuild` deletes that tenant's `projection_checkpoints` rows.
   */
  readonly worst: PlatformIndexCheckpointSummary | undefined;
  /** How far behind `worst` is, or `undefined` when no checkpoint of this alias has an instant. */
  readonly lagMs: number | undefined;
  /** Rebuild requests for this alias in status `requested`, which is the in-flight one. */
  readonly rebuilding: readonly PlatformIndexRebuildSummary[];
  /** Every rebuild request for this alias, `engine_reset` ones included. */
  readonly rebuilds: readonly PlatformIndexRebuildSummary[];
  readonly deadLetters: readonly PlatformIndexDeadLetterSummary[];
}

/**
 * Group the three listings by index alias, which is the grain the design draws.
 *
 * The alias order is **first appearance across the three listings in the order they are named**,
 * checkpoints first. Each listing is already ordered by its own primary key with `tenant_id`
 * leading, so no alias order is the service's to preserve — but a stable, explicable one is, and
 * re-sorting alphabetically here would quietly replace the `default` order `board.ts` promises not
 * to touch.
 *
 * An alias reached by a rebuild or a dead letter and by no checkpoint is still a row. That is the
 * design's own `drivers`: mid-rebuild, with its checkpoints deleted, `—` in the `Lag` cell. A board
 * built only from checkpoints would have dropped it.
 */
export function rollUp(listings: IndexListings, now: number): readonly IndexRollUp[] {
  const order: string[] = [];
  const see = (alias: string): void => {
    if (!order.includes(alias)) order.push(alias);
  };
  for (const checkpoint of listings.checkpoints ?? []) see(checkpoint.indexAlias);
  for (const rebuild of listings.rebuilds ?? []) see(rebuild.indexAlias);
  for (const deadLetter of listings.deadLetters ?? []) see(deadLetter.indexAlias);

  return order.map((alias) => {
    const checkpoints = (listings.checkpoints ?? []).filter(
      (checkpoint) => checkpoint.indexAlias === alias,
    );
    const rebuilds = (listings.rebuilds ?? []).filter((rebuild) => rebuild.indexAlias === alias);

    let worst: PlatformIndexCheckpointSummary | undefined = undefined;
    let lagMs: number | undefined = undefined;
    for (const checkpoint of checkpoints) {
      if (checkpoint.indexedThrough === null) continue;
      const indexedThrough = Date.parse(checkpoint.indexedThrough);
      if (!Number.isFinite(indexedThrough)) continue;
      const lag = Math.max(0, now - indexedThrough);
      if (lagMs === undefined || lag > lagMs) {
        lagMs = lag;
        worst = checkpoint;
      }
    }

    return {
      alias,
      checkpoints,
      // No checkpoint carries an instant, so the cells fall back to the first row the listing
      // answered for this alias: an offset with no lag beside it is still the design's `Checkpoint`.
      worst: worst ?? checkpoints[0],
      lagMs,
      rebuilding: rebuilds.filter((rebuild) => rebuild.status === "requested"),
      rebuilds,
      deadLetters: (listings.deadLetters ?? []).filter(
        (deadLetter) => deadLetter.indexAlias === alias,
      ),
    };
  });
}

/**
 * The badge's class and word, in the design's own vocabulary where it has one.
 *
 * | condition                                | class     | word            | whose word                 |
 * | ---------------------------------------- | --------- | --------------- | -------------------------- |
 * | a rebuild request in status `requested`  | `active`  | `rebuilding`    | the design's second row     |
 * | else any dead-lettered projection event  | `failed`  | `dead-lettered` | the design's fourth tile    |
 * | else a lag of `CURRENT_WITHIN_MS` or more | `caution` | `lagging`       | the design's first row      |
 * | else                                      | `done`    | `current`       | the design's third row      |
 *
 * **`rebuilding` is tested first and that is a decision**, because an alias can be rebuilding and
 * dead-lettered at once. Two reasons, and both are about not making the row contradict itself: the
 * `Checkpoint` cell already says `rebuilding` for such a row, so a badge saying `dead-lettered`
 * beside it would be two statements fighting on one line; and a rebuild is the thing somebody is
 * already doing about the index, which is what an operator scanning the board needs to know first.
 * The dead-letter count is on the panel for every row either way, so nothing is hidden by the order.
 *
 * **`dead-lettered` is the design's own tile word**, lower-cased to match the vocabulary of the row
 * badges beside it, taking the `failed` class its tile has. The design draws no row in this state —
 * its three rows are `lagging`, `rebuilding` and `current` — and a board of real indexes reaches it
 * whenever a projection fails five times. `event-catalogue.ts` records the same split: the words the
 * design drew are copied, and a state it did not draw is given the nearest word the design itself
 * uses rather than one invented here.
 *
 * A state is never shown by colour alone: four classes, four glyphs, four different words.
 */
export function stateOf(index: IndexRollUp): {
  classKey: StateClassKey;
  label: IndexState;
  title: string;
} {
  if (index.rebuilding.length > 0) {
    return {
      classKey: "active",
      label: "rebuilding",
      title: `${String(index.rebuilding.length)} rebuild request${index.rebuilding.length === 1 ? "" : "s"} in status requested. The design writes a progress figure here — rebuild 61% — and SEA-02 models none: rebuild_requests.status is requested or engine_reset and nothing else. ADR 0064 §8, Q-221.`,
    };
  }
  if (index.deadLetters.length > 0) {
    return {
      classKey: "failed",
      label: "dead-lettered",
      title: `${String(index.deadLetters.length)} projection event${index.deadLetters.length === 1 ? "" : "s"} could not be indexed after five attempts. The error each one carries is withheld: it is an arbitrary exception message truncated to a hundred characters and nothing constrains it to be free of document content. ADR 0064 §5.`,
    };
  }
  const lagMs = index.lagMs;
  if (lagMs !== undefined && lagMs >= CURRENT_WITHIN_MS) {
    return {
      classKey: "caution",
      label: "lagging",
      title: `Behind by ${asSpan(lagMs)}, measured as now − indexedThrough. SEA-02 publishes no freshness objective, so the line between lagging and current is this console's own STALE_AFTER_MS of ${asSpan(CURRENT_WITHIN_MS)} rather than a published threshold. The duration beside this badge is measured; the word is a rendering.`,
    };
  }
  return {
    classKey: "done",
    label: "current",
    title:
      lagMs === undefined
        ? "No checkpoint of this index carries an indexedThrough instant, so no lag is measurable and no rebuild or dead letter is outstanding."
        : `Behind by ${asSpan(lagMs)}, measured as now − indexedThrough, which is under this console's own STALE_AFTER_MS of ${asSpan(CURRENT_WITHIN_MS)}. SEA-02 publishes no freshness objective, so the word is a rendering and the duration beside it is the measurement.`,
  };
}

/** The cells and panel of one index alias, as the design draws them. */
export function indexRow(index: IndexRollUp, listings: IndexListings): BoardRow {
  const state = stateOf(index);
  const worst = index.worst;
  const tenants = new Set(index.checkpoints.map((checkpoint) => checkpoint.tenantId)).size;
  const sources = new Set(index.checkpoints.map((checkpoint) => checkpoint.source)).size;

  /*
   * The `Checkpoint` cell, which is three different cells in the design and two here.
   *
   * `offset 1,882,104` is the offset of the checkpoint the lag was measured from, in the design's
   * own word and its own grouping. `rebuild 61%` becomes the word `rebuilding` with no figure — ADR
   * 0064 §8, Q-221. And an alias with no checkpoint and no open rebuild draws the design's own `—`.
   */
  const checkpointCell =
    index.rebuilding.length > 0
      ? {
          text: "rebuilding",
          title: `An open rebuild request. The design writes rebuild 61% here; rebuild_requests.status is requested or engine_reset and no percentage exists in the table, the domain or the engine writer, so the figure is not drawn. ADR 0064 §8, Q-221.`,
        }
      : worst === undefined
        ? {
            text: "",
            absent: true,
            title:
              "No projection checkpoint has been written for this index on any tenant. The alias is on this board because a rebuild request or a dead-lettered event names it.",
          }
        : {
            text: `offset ${grouped.format(worst.lastSequence)}`,
            mono: true,
            title: `projection_checkpoints.last_sequence for tenant ${worst.tenantId}, source ${worst.source} — the checkpoint this row's Lag is measured from. The alias holds ${String(index.checkpoints.length)} checkpoint${index.checkpoints.length === 1 ? "" : "s"} at the table's own (tenant, alias, source) grain; the board rolls up to the worst lag. ADR 0064 §4, Q-220.`,
          };

  /*
   * The `Lag` cell, which is the half of the recorded absence that was route work.
   *
   * `now − indexedThrough`, in the design's own `42 s`. Drawn whenever a checkpoint of this alias
   * carries the instant, **including while a rebuild is open** — the design's rebuilding row shows
   * `—` because an engine reset deletes that tenant's checkpoint rows, which is the absence below
   * rather than a rule about rebuilds. ADR 0064 §1: the board subtracts.
   */
  const lagMs = index.lagMs;
  const lagCell =
    lagMs === undefined
      ? {
          text: "",
          absent: true,
          title:
            "No checkpoint of this index carries an indexedThrough instant, so there is nothing to subtract from now. An index mid-rebuild reads this way: completeRebuild deletes that tenant's projection_checkpoints rows.",
        }
      : {
          text: asSpan(lagMs),
          title: `now − indexedThrough, where indexedThrough is the source event's own occurredAt (${worst === undefined ? "" : asMoment(worst.indexedThrough ?? "")}). SEA-02 publishes no lag field and should not: a lag computed in a service is stale before it is rendered. ADR 0064 §1.`,
        };

  return {
    // The alias, which is this board's grain and is unique across it by construction.
    id: index.alias,
    cells: [
      {
        text: index.alias,
        /*
         * Not mono, which is the design's own rule read literally and agreed with. Its grid takes
         * Geist Mono for a value matching `/^(ten_|evt_|RPL|RSK|wf_|\/v1|key|offset)/`, and its own
         * cells here are `orders`, `drivers`, `merchants` — none of which matches. An index alias is
         * a configured word rather than an identifier, so Geist is right for it as well as copied.
         */
        title: `Every tenant's projections into the ${index.alias} index, rolled up from ${String(index.checkpoints.length)} checkpoint${index.checkpoints.length === 1 ? "" : "s"} across ${String(tenants)} tenant${tenants === 1 ? "" : "s"}. The design draws one row per alias and the table's grain is (tenant, alias, source). ADR 0064 §4, Q-220.`,
      },
      checkpointCell,
      lagCell,
    ],
    state,
    /*
     * No row tint. The design tints its `human` and `failed` classes — `--humanRow` and `--failRow`
     * — and this console's stylesheet carries a rule for the first only, which `event-catalogue.ts`
     * records as part of Q-217. No row of this board can be `human`: nothing on this page waits on a
     * named person. A `dead-lettered` row is `failed`, so it takes the same untinted treatment every
     * `failed` row in this console takes, and its badge carries the glyph and the word.
     */
    detail: panelFields(index, listings, tenants, sources),
    /*
     * The design's `History` block, with the one entry this record genuinely has.
     *
     * `Created` and `Version` are `—` and that is measured rather than lazy: `projection_checkpoints`
     * has `tenant_id`, `index_alias`, `source`, `last_sequence`, `indexed_through` and `updated_at`
     * and no creation instant and no version column at all, and a row of this board is a roll-up of
     * several of them in any case. The two names the design writes beside the first two entries —
     * `W. Mutua`, `K. Otieno` — are display names the platform does not hold anywhere; Q-107.
     */
    history: [
      {
        label: "Created",
        value: "",
        absent: true,
      },
      {
        label: "Last changed",
        ...(lastChanged(index) === undefined
          ? { value: "", absent: true }
          : { value: asMoment(lastChanged(index) ?? "") }),
      },
      { label: "Version", value: "", absent: true },
    ],
    /*
     * The design's own glyph for this panel: its head renders `fm.icon`, and `fm.icon` comes from
     * the page's entry in `FORMS`, which for `search` is `manage_search`. The same glyph the rail
     * uses for this page, which is a coincidence of this page rather than a rule — `dlq` has no
     * `FORMS` entry at all and draws `info`.
     */
    glyph: "manage_search",
  };
}

/** The latest `updated_at` across the alias's checkpoints, which is the only instant it has. */
function lastChanged(index: IndexRollUp): string | undefined {
  let latest: string | undefined = undefined;
  let at = Number.NEGATIVE_INFINITY;
  for (const checkpoint of index.checkpoints) {
    const parsed = Date.parse(checkpoint.updatedAt);
    if (Number.isFinite(parsed) && parsed > at) {
      at = parsed;
      latest = checkpoint.updatedAt;
    }
  }
  return latest;
}

/**
 * The panel's lines beyond the grid's three columns.
 *
 * The design's grid has no tenant in it, which is correct for a board of configured aliases and
 * leaves an operator unable to tell whose projection is behind. The panel is where the design keeps
 * what a row's columns cannot carry, so that is where the roll-up is explained: how many checkpoints
 * and how many tenants went into the two cells above, and which tenant and source the worst of them
 * belongs to.
 *
 * A line whose listing did not answer draws the design's own `—` rather than being dropped, for
 * `event-catalogue.ts`'s reason: a panel whose field table changes shape row by row makes an absence
 * look like a different record. The board's footnote says which listing was missing, because
 * `DetailField` carries no `title`.
 */
function panelFields(
  index: IndexRollUp,
  listings: IndexListings,
  tenants: number,
  sources: number,
): readonly DetailField[] {
  const worst = index.worst;
  const fields: DetailField[] = [
    {
      label: "Checkpoints",
      value: `${String(index.checkpoints.length)} across ${String(tenants)} tenant${tenants === 1 ? "" : "s"} and ${String(sources)} source${sources === 1 ? "" : "s"}`,
      ...(listings.checkpoints === undefined ? { absent: true } : {}),
    },
    {
      label: "Slowest tenant",
      ...(worst === undefined
        ? { value: "", absent: true }
        : { value: worst.tenantId, mono: true as const }),
    },
    {
      label: "Slowest source",
      ...(worst === undefined
        ? { value: "", absent: true }
        : { value: worst.source, mono: true as const }),
    },
    {
      label: "Indexed through",
      ...(worst === undefined || worst.indexedThrough === null
        ? { value: "", absent: true }
        : { value: asMoment(worst.indexedThrough) }),
    },
  ];

  if (listings.rebuilds === undefined) {
    fields.push({ label: "Rebuilds requested", value: "", absent: true });
  } else {
    fields.push({
      label: "Rebuilds requested",
      // Both statuses, because `engine_reset` is the completed half of the same request rather than
      // a second kind of thing: `completeRebuild` sets it after the engine has been reset and the
      // tenant's inbox and checkpoints deleted. The in-flight count is what the badge is drawn from.
      value: `${String(index.rebuilding.length)} open of ${String(index.rebuilds.length)}`,
    });
  }

  if (listings.deadLetters === undefined) {
    fields.push({ label: "Dead-lettered events", value: "", absent: true });
  } else {
    fields.push({
      label: "Dead-lettered events",
      value: String(index.deadLetters.length),
    });
  }

  return fields;
}

/** Every row the board holds, in the design's grain. */
export function indexRows(listings: IndexListings, now: number): readonly BoardRow[] {
  return rollUp(listings, now).map((index) => indexRow(index, listings));
}

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(rows: readonly BoardRow[]): readonly IndexState[] {
  const present = new Set(rows.map((row) => row.state.label));
  return INDEX_STATES.filter((label) => present.has(label));
}

/**
 * What the board owes an operator about the three listings behind it, in one sentence each.
 *
 * Three things, each said rather than implied:
 *
 *   - **how many tenants and checkpoints the rows were rolled up from**, because the design's grid
 *     has no tenant column and an alias on this board is every tenant's projection into it. Q-220 is
 *     exactly this sentence.
 *   - **which of the three listings did not answer**, because two of them are a badge and two panel
 *     lines rather than the grid, and an operator who never opens a row would not otherwise learn
 *     that a listing was missing.
 *   - **that no `State` threshold is published**, which is the one thing on this board a reader
 *     cannot discover by hovering a cell they did not think to hover.
 */
export function listingNote(
  listings: IndexListings,
  rows: readonly BoardRow[],
): string | undefined {
  if (listings.checkpoints === undefined) return undefined;

  const tenants = new Set(listings.checkpoints.map((checkpoint) => checkpoint.tenantId)).size;
  const span = `${String(rows.length)} index${rows.length === 1 ? "" : "es"}, rolled up from ${String(listings.checkpoints.length)} checkpoint${listings.checkpoints.length === 1 ? "" : "s"} across ${String(tenants)} tenant${tenants === 1 ? "" : "s"}: the design draws one row per index and SEA-02 publishes at its table's (tenant, index, source) grain.`;

  const missing: string[] = [];
  if (listings.rebuilds === undefined) missing.push("rebuild requests");
  if (listings.deadLetters === undefined) missing.push("dead-lettered events");
  const gap =
    missing.length === 0
      ? ""
      : ` SEA-02's ${missing.join(" and ")} listing${missing.length === 1 ? "" : "s"} did not answer, so no row is shown as ${missing.length === 1 && missing[0] === "rebuild requests" ? "rebuilding" : "dead-lettered"} and those panel lines draw —.`;

  const threshold = ` The lag is measured as now − indexedThrough; the line between lagging and current is this console's own ${asSpan(CURRENT_WITHIN_MS)}, because SEA-02 publishes no freshness objective.`;

  return `${span}${gap}${threshold}`;
}

/**
 * Why this page's primary command is drawn and refused.
 *
 * **The design draws it**, unlike `dlq`: `FORMS.search` is `['Rebuild index','Rebuild a search
 * index','manage_search','The live index keeps serving until the rebuild catches up.', …]`, so its
 * `formUI` returns a visible button and its panel draws `Edit` beside it. `CLAUDE.md` §1 forbids
 * removing a control the design draws as plainly as it forbids adding one, so it is drawn, disabled,
 * with the measured reason on it.
 *
 * Three reasons, each sufficient on its own:
 *
 *   - **The rebuild command is `/internal/v1` only.** ADR 0064 §7 publishes no write under
 *     `/v1/platform` and the router answers 404 on the platform-shaped path, deliberately.
 *   - **The form's `Tenant` select is the decision a cross-tenant write would have to take.** Its
 *     options are `All tenants|ten_7Q2M|ten_3KD9|ten_Q81X` — a platform operator choosing whose
 *     index to rebuild, which is exactly what ADR 0064 §7 declines to publish.
 *   - **A rebuild is destructive in a way nothing else in this cohort is.** `completeRebuild`
 *     deletes that tenant's `projection_inbox` and `projection_checkpoints` rows.
 */
export const REBUILD_REFUSAL =
  "SEA-02 publishes no rebuild under /v1/platform: the command is on /internal/v1, which the gateway does not expose, and the design's form asks a platform operator to choose whose index to rebuild — the cross-tenant write ADR 0064 §7 declines. A rebuild also deletes that tenant's projection inbox and checkpoints.";
